import { GetServerSidePropsContext } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import Head from "next/head";
import { useRouter } from "next/router";
import { FC, useState, useRef } from "react";
import { z } from "zod";

import { APP_NAME } from "@calcom/lib/constants";
import { Steps } from "@calcom/ui";
import { useLocale } from "@calcom/lib/hooks/useLocale";

import { inferSSRProps } from "@lib/types/inferSSRProps";
import { OnboardingLayout } from "@components/onboarding/layout";
import { GoalStep } from "@components/onboarding/steps-views/GoalStep";
import { HurdleStep } from "@components/onboarding/steps-views/HurdleStep";
import { InsuranceStep } from "@components/onboarding/steps-views/InsuranceStep";
import { EmailStep } from "@components/onboarding/steps-views/EmailStep";
import { CodeStep } from "@components/onboarding/steps-views/CodeStep";
import { CoachStep } from "@components/onboarding/steps-views/CoachStep";
import { useSession } from "next-auth/react";
import { trpc } from "@calcom/trpc";
import { getSession } from "@calcom/lib/auth";

export type IOnboardingPageProps = inferSSRProps<typeof getServerSideProps>;

export type IOnboardingComponentProps = {
  nextStep: () => void;
  handleSaveAnswer: (key: string, value: any) => void;
  email?: string;
  name?: string;
  setEmail?: (email: string) => void;
  setName?: (name?: string) => void;
  onboardingData?: Record<string, any>;
  isEmbed?: boolean;
};

export type OnboardingUrl = (typeof availableUrls)[number];

type StepLink = { [key in OnboardingUrl]: { next: OnboardingUrl | null } };

const stepLinks: StepLink = {
  goal: { next: "hurdle" },
  hurdle: { next: "insurance" },
  insurance: { next: "email" },
  email: { next: "code" },
  code: { next: "coaches" },
  coaches: { next: null },
};

type Step = {
  url: OnboardingUrl;
  headers: {
    title: string;
    subtitle: string;
  };
  sendEventOnVisit?: boolean;
  eventName?: string;
  attributes?: Record<string, any>;
};

const availableUrls = ["goal", "hurdle", "insurance", "email", "code", "coaches"] as const;

const availableSteps: Step[] = [
  {
    url: "goal",
    headers: {
      title: "lb_onboarding_goal_title",
      subtitle: "lb_onboarding_goal_subtitle",
    },
    sendEventOnVisit: true,
    eventName: "form_start",
    attributes: {
      form_step: "goal",
      form_key: "onboarding",
    },
  },
  {
    url: "hurdle",
    headers: {
      title: "lb_onboarding_hurdle_title",
      subtitle: "lb_onboarding_hurdle_subtitle",
    },
    sendEventOnVisit: true,
    eventName: "form_step",
    attributes: {
      form_step: "hurdle",
      form_key: "onboarding",
    },
  },
  {
    url: "insurance",
    headers: {
      title: "lb_onboarding_insurance_title",
      subtitle: "lb_onboarding_insurance_subtitle",
    },
    sendEventOnVisit: true,
    eventName: "form_step",
    attributes: {
      form_step: "insurance",
      form_key: "onboarding",
    },
  },
  {
    url: "email",
    headers: {
      title: "lb_onboarding_email_title",
      subtitle: "lb_onboarding_email_subtitle",
    },
    sendEventOnVisit: true,
    eventName: "form_submit",
  },
  {
    url: "code",
    headers: {
      title: "lb_onboarding_code_title",
      subtitle: "lb_onboarding_code_subtitle",
    },
  },
  {
    url: "coaches",
    headers: {
      title: "lb_onboarding_coaches_title",
      subtitle: "lb_onboarding_coaches_subtitle",
    },
  },
];
const skippedAuthUrls = ["email", "code"] as const;
const skippedAuthUrlsIndices = skippedAuthUrls.map((url) => availableUrls.indexOf(url));

const components: Record<OnboardingUrl, FC<IOnboardingComponentProps>> = {
  goal: GoalStep,
  hurdle: HurdleStep,
  insurance: InsuranceStep,
  email: EmailStep,
  code: CodeStep,
  coaches: CoachStep,
};

const stepRouteSchema = z.object({ step: z.array(z.enum(availableUrls)) });

const stepTransform = (step: Step, allSteps: Step[]) => {
  const idx = allSteps.findIndex((s) => s.url === step.url);
  return idx > -1 ? allSteps[idx].url : allSteps[0].url;
};

const OnboardingPage = (props: IOnboardingPageProps) => {
  const router = useRouter();
  const { isEmbed } = props;
  const { t } = useLocale();
  const [email, setEmail] = useState("");
  const [name, setName] = useState<string | undefined>();
  const onboardingData = useRef<Record<string, any>>({});
  const result = stepRouteSchema.safeParse(router.query);
  const currentUrl = result.success ? result.data.step[0] : availableSteps[0].url;
  const currentStepIndex = availableSteps.findIndex((s) => s.url === currentUrl);
  const { status } = useSession();
  const savedAnswers = trpc.viewer.onboarding.saveOnboardingAnswers.useMutation();

  const handleSaveAnswer = (key: string, value: any) => {
    onboardingData.current = { ...onboardingData.current, [key]: value };
  };

  const goToIndex = (index: number) => {
    const newStep = availableSteps[index];
    router.push({
      pathname: `/onboarding/${stepTransform(newStep, availableSteps)}`,
      query: { embed: isEmbed ? "1" : undefined },
    });
  };

  const goToNextStep = async (currentStepIndex: number) => {
    const currentStep = availableSteps[currentStepIndex];
    const { next } = stepLinks[currentStep.url];
    if (currentStep.sendEventOnVisit && currentStep.eventName) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).dataLayer.push({ event: currentStep.eventName, ...currentStep.attributes });
      } catch (e) {
        console.error("dataLayer.push error", e);
      }
    }

    if (currentStep.url === "code") {
      await savedAnswers.mutate({
        goal: onboardingData.current.goal,
        hurdle: onboardingData.current.hurdle,
        insurance: onboardingData.current.insurance,
      });
    }

    if (next == null) {
      if (isEmbed) {
        window.parent?.postMessage({ type: "lb_onboarding_complete", answers: onboardingData }, "*");
      } else {
        router.push({ pathname: "/search" });
      }
      return;
    }
    if (status === "authenticated" && skippedAuthUrls.includes(next as (typeof skippedAuthUrls)[number])) {
      goToNextStep(currentStepIndex + 1);
      return;
    }

    router.push({ pathname: `/onboarding/${next}`, query: { embed: isEmbed ? "1" : undefined } });
  };

  const { headers, url } = availableSteps[currentStepIndex];
  const Component = components[url];

  const stepProps = {
    handleSaveAnswer,
    onboardingData,
    ...(url === "email" && { setEmail, setName }),
    ...(url === "code" && { email, name }),
    isEmbed,
  };

  return (
    <OnboardingLayout isEmbed={isEmbed}>
      <div data-testid="onboarding-v2" key={currentUrl}>
        <Head>
          <title>{t("lb_onboarding_page_title", { appName: APP_NAME })}</title>
          <link rel="icon" href="/favicon.ico" />
        </Head>

        <div className="relative">
          <Steps
            maxSteps={availableSteps.length}
            currentStep={currentStepIndex + 1}
            navigateToStep={goToIndex}
            unclickableIndices={skippedAuthUrlsIndices}
          />
          <div>
            <p className="font-cal mb-3 text-xl font-medium leading-7 md:text-2xl">{t(headers.title)}</p>
            <p className="font-sans text-sm font-normal text-gray-500">{t(headers.subtitle)}</p>
          </div>
        </div>

        <div className="mt-8 ">
          <Component nextStep={() => goToNextStep(currentStepIndex)} {...stepProps} />
        </div>
      </div>
    </OnboardingLayout>
  );
};

export const getServerSideProps = async (context: GetServerSidePropsContext) => {
  const isEmbed = context.query.embed === "1";

  try {
    const {
      step: [s],
    } = stepRouteSchema.parse(context.query);
    if (!availableUrls.includes(s)) return { notFound: true };
  } catch {
    if (context.query.step != null) return { notFound: true };
  }
  const session = await getSession(context);
  const user = session?.user;
  const local = user ? context.locale ?? "de" : "de";

  return {
    props: {
      ...(await serverSideTranslations(local, ["common"])),
      isEmbed,
    },
  };
};

export default OnboardingPage;
